import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import walkthrough from "./data/walkthrough.json";
import {
  textToSpeech,
  cancelTTS,
  isOpenAIAvailable,
  startListening,
  stopListening,
  matchPhrase,
} from "./OpenAIVoiceService";

// WalkthroughContext provides the guided-demo state machine and side-effect hooks.
const WalkthroughContext = createContext(null);

const defaultPersistKey = (walk) =>
  walk?.initialSettings?.persistKey || "echo_walkthrough_v1";

function reducer(state, action) {
  switch (action.type) {
    case "LOAD":
      return {
        ...state,
        steps: action.steps,
        settings: action.settings,
      };
    case "SET_INDEX":
      return { ...state, currentStepIndex: action.index };
    case "TOGGLE_LISTENING":
      return { ...state, listening: !state.listening };
    case "SET_LISTENING":
      return { ...state, listening: !!action.value };
    case "TOGGLE_EXPLAINER":
      return { ...state, explainer: !state.explainer };
    case "SET_EXPLAINER":
      return { ...state, explainer: !!action.value };
    case "RESET":
      return {
        ...state,
        currentStepIndex: 0,
        listening: state.settings?.listening ?? false,
        explainer: state.settings?.explainer ?? true,
      };
    default:
      return state;
  }
}

export function WalkthroughProvider({ children, handlers = {} }) {
  const persistKey = defaultPersistKey(walkthrough);

  const [state, dispatch] = useReducer(reducer, {
    steps: [],
    settings: {},
    currentStepIndex: 0,
    listening: false,
    explainer: true,
    isRunning: true,
  });

  // refs for speech session
  const speechActiveRef = useRef(false);
  const enteredRef = useRef(new Set()); // track steps we've already executed onEnter for
  const listeningActiveRef = useRef(false); // track if we're currently listening for speech input
  const currentStepRef = useRef(null); // track current step for speech recognition

  useEffect(() => {
    // load JSON and persisted settings
    const steps = walkthrough.steps || [];
    const settings = walkthrough.initialSettings || {};

    // persisted user choices
    const persisted = JSON.parse(localStorage.getItem(persistKey) || "{}");

    dispatch({ type: "LOAD", steps, settings });

    // apply persisted choices
    if (persisted.listening !== undefined)
      dispatch({ type: "SET_LISTENING", value: persisted.listening });
    else
      dispatch({ type: "SET_LISTENING", value: settings.listening ?? false });

    if (persisted.explainer !== undefined)
      dispatch({ type: "SET_EXPLAINER", value: persisted.explainer });
    else dispatch({ type: "SET_EXPLAINER", value: settings.explainer ?? true });

  }, []);

  // Cleanup: stop listening when component unmounts
  useEffect(() => {
    return () => {
      if (listeningActiveRef.current) {
        stopListening();
        listeningActiveRef.current = false;
      }
    };
  }, []);

  // Stop listening when listening mode is toggled off
  useEffect(() => {
    if (!state.listening && listeningActiveRef.current) {
      stopListening();
      listeningActiveRef.current = false;
    }
  }, [state.listening]);

  // Whenever the currentStepIndex changes, run the side-effects for that step.
  useEffect(() => {
    const idx = state.currentStepIndex;
    const steps = state.steps || [];
    if (typeof idx !== "number" || idx < 0 || idx >= steps.length) return;
    if (enteredRef.current.has(idx)) return;
    // call enterStep to run onEnter actions and side-effects
    enterStep(idx);
  }, [state.currentStepIndex, state.steps]);

  // Sync current step with external handler
  useEffect(() => {
    const currentStep = (state.steps || [])[state.currentStepIndex] || null;
    if (handlers?.setCurrentStep) {
      handlers.setCurrentStep(currentStep);
    }
  }, [state.currentStepIndex, state.steps, handlers]);

  const persistChoices = (extra = {}) => {
    const prev = JSON.parse(localStorage.getItem(persistKey) || "{}");
    const next = {
      ...prev,
      listening: state.listening,
      explainer: state.explainer,
      ...extra,
    };
    localStorage.setItem(persistKey, JSON.stringify(next));
  };

  // helper: run a list of actions from a step
  const runActions = async (actions = []) => {
    if (!Array.isArray(actions) || actions.length === 0) return;
    for (const action of actions) {
      try {
        // console.log('[walkthrough] runAction', action);
        switch (action.type) {
          case "message":
            if (handlers.dispatchMessage && action.message) {
              if (
                action.message.type &&
                (action.message.type === "ADD_MESSAGE" ||
                  action.message.type === "SET_MESSAGES")
              ) {
                // already an action for the reducer
                handlers.dispatchMessage(action.message);
              } else {
                handlers.dispatchMessage({
                  type: "ADD_MESSAGE",
                  message: action.message,
                });
              }
            }
            break;
          case "tts":
            if (handlers.tts) {
              handlers.tts(action.text);
            } else {
              // Try OpenAI TTS first, fallback to browser speech synthesis
              try {
                if (isOpenAIAvailable()) {
                  await textToSpeech(action.text || "", "alloy");
                } else {
                  throw new Error("OpenAI API not available");
                }
              } catch (e) {
                console.warn("OpenAI TTS failed, falling back to browser:", e);
                // Fallback to browser speech synthesis
                if (window && window.speechSynthesis) {
                  try {
                    const ut = new SpeechSynthesisUtterance(action.text || "");
                    window.speechSynthesis.cancel();
                    window.speechSynthesis.speak(ut);
                  } catch (e2) {
                    console.warn("Browser TTS also failed", e2);
                  }
                }
              }
            }
            break;
          case "fly":
            if (handlers.flyTo) {
              // expect coords, height, orientationDegrees
              const [lat, lon] = action.coords;
              const height = action.height;
              const d2r = Math.PI / 180.0;
              const orientation = {
                heading: action.orientationDegrees.heading * d2r,
                pitch: action.orientationDegrees.pitch * d2r,
                roll: action.orientationDegrees.roll * d2r,
              };
              handlers.flyTo([lat, lon], height, orientation);
            }
            break;
          case "ui":
            if (handlers.ui) {
              const uiAction = action.action || action;
              try {
                // Don't await UI actions - they should be non-blocking
                // This allows camera movements and effects to happen immediately
                handlers.ui(uiAction);
              } catch (e) {
                // console.warn("[walkthrough] handlers.ui rejected", e);
              }
            }
            break;
          default:
            console.log("Unhandled walk action", action);
        }
      } catch (err) {
        console.error("Error running action", action, err);
      }
    }
  };

  const enterStep = (index) => {
    const steps = state.steps || [];
    if (index < 0 || index >= steps.length) return;
    const step = steps[index];

    // Stop any playing audio from previous step
    try {
      cancelTTS();
      if (window && window.speechSynthesis) window.speechSynthesis.cancel();
    } catch (e) {
      // Silently fail - audio might not be playing
    }

    // Debug: log step and explainer state
    try {
      const explOnly = step?.ui?.explainerOnly;
      // console.log('[walkthrough] enterStep called', {
      //   index,
      //   stepId: step?.id,
      //   explainer: state.explainer,
      //   explainerOnly: explOnly,
      //   alreadyEntered: enteredRef.current.has(index),
      // });
    } catch (e) {
      /* ignore logging errors */
    }
    // If this step is explainer-only but explainer mode is OFF, skip it entirely
    // and advance to the next step without running its onEnter actions.
    try {
      const explOnlyFlag = step?.ui?.explainerOnly;
      if (explOnlyFlag && !state.explainer) {
        // console.log('[walkthrough] skipping explainer-only step', { index, stepId: step?.id, explainerOnly: explOnlyFlag });
        enteredRef.current.add(index);
        const nextIndex = index + 1;
        // console.log('[walkthrough] advancing to next index', { nextIndex });
        if (nextIndex < steps.length) dispatch({ type: "SET_INDEX", index: nextIndex });
        return;
      }
      // If this step is instruction type but listening mode is OFF, skip it entirely
      // and advance to the next step without running its onEnter actions.
      const messageData = step?.onEnter?.message?.data;
      // const startsWithSay = typeof messageData === 'string' && messageData.startsWith('Say');
      // const isInstructionType = step?.type === "instruction" && startsWithSay;
      const isInstructionType = step?.type === "instruction";
      if (isInstructionType && !state.listening) {
        enteredRef.current.add(index);
        const nextIndex = index + 1;
        if (nextIndex < steps.length) dispatch({ type: "SET_INDEX", index: nextIndex });
        return;
      }
    } catch (e) {
      /* ignore */
    }

    // Avoid re-running actions for a step we've already entered
    if (enteredRef.current.has(index)) {
      // console.log(
      //   "[walkthrough] enterStep skipped (already entered) index=",
      //   index,
      //   "stepId=",
      //   step?.id
      // );
      return;
    }

    // console.log("[walkthrough] enterStep index=", index, "stepId=", step?.id);

    // apply chatState
    if (step.chatState && handlers.setChatState)
      handlers.setChatState(step.chatState);

    // showBubble
    try {
      const wantsChat =
        Array.isArray(step?.ui?.showComponents) &&
        step.ui.showComponents.includes("SpeechBubbleChat");
      if (wantsChat && handlers.setShowBubble) {
        // console.log(
        //   "[walkthrough] requesting showBubble = true for step",
        //   step.id
        // );
        handlers.setShowBubble(true);
      }
    } catch (e) {
      // console.warn("[walkthrough] error checking showComponents", e);
    }

    if (step.onEnter) {
      // console.log("[walkthrough] running onEnter actions for", step.id, step.onEnter);
      runActions(step.onEnter);
    }

    // Handle speech input
    if ((step.expectedInput || "").includes("speech")) {
      currentStepRef.current = step;

      // Start continuous listening if listening mode is enabled
      if (state.listening && !listeningActiveRef.current) {
        listeningActiveRef.current = true;

        // Extract expected phrases from step
        const expectedPhrases = step.expectedPhrases || [];

        startListening(
          (transcribedText) => {
            console.log("[Walkthrough] Transcribed:", transcribedText);

            // Match against expected phrases
            const match = matchPhrase(transcribedText, expectedPhrases, 0.6);

            if (match) {
              console.log("[Walkthrough] Phrase matched:", match);

              // Stop listening
              stopListening();
              listeningActiveRef.current = false;

              // Execute the step's onSpeechMatch actions if defined
              if (step.onSpeechMatch) {
                runActions(step.onSpeechMatch);
              }

              // Auto-advance to next step
              next();
            }
          },
          (error) => {
            console.error("[Walkthrough] Speech recognition error:", error);
            listeningActiveRef.current = false;
          }
        ).catch((error) => {
          console.error("[Walkthrough] Failed to start listening:", error);
          listeningActiveRef.current = false;
        });
      }
    } else {
      // Stop listening if step doesn't expect speech input
      if (listeningActiveRef.current) {
        stopListening();
        listeningActiveRef.current = false;
      }
    }

    enteredRef.current.add(index);
    // console.log('[walkthrough] marked step entered', { index, stepId: step?.id });
  };

  const next = () => {
    // Stop any playing audio when advancing to next step
    try {
      // Cancel OpenAI TTS if available
      cancelTTS();
    } catch (e) {
      console.warn("Error cancelling TTS:", e);
    }

    // Cancel browser speech synthesis if available
    if (window && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {
        console.warn("Error cancelling browser TTS:", e);
      }
    }

    const nextIndex = (state.currentStepIndex || 0) + 1;
    if (nextIndex >= (state.steps || []).length) return;
    dispatch({ type: "SET_INDEX", index: nextIndex });
  };

  const prev = () => {
    // Stop any playing audio when going back
    try {
      cancelTTS();
      if (window && window.speechSynthesis) window.speechSynthesis.cancel();
    } catch (e) {
      console.warn("Error cancelling audio:", e);
    }

    const prevIndex = Math.max(0, (state.currentStepIndex || 0) - 1);
    dispatch({ type: "SET_INDEX", index: prevIndex });
  };

  const goTo = (idOrIndex) => {
    // Stop any playing audio when jumping to a step
    try {
      cancelTTS();
      if (window && window.speechSynthesis) window.speechSynthesis.cancel();
    } catch (e) {
      console.warn("Error cancelling audio:", e);
    }

    const steps = state.steps || [];
    if (typeof idOrIndex === "number")
      return dispatch({ type: "SET_INDEX", index: idOrIndex });
    const idx = steps.findIndex((s) => s.id === idOrIndex);
    if (idx >= 0) dispatch({ type: "SET_INDEX", index: idx });
  };

  const toggleListening = () => {
    dispatch({ type: "TOGGLE_LISTENING" });
    setTimeout(() => persistChoices(), 0);
  };

  const toggleExplainer = () => {
    dispatch({ type: "TOGGLE_EXPLAINER" });
    setTimeout(() => persistChoices(), 0);
  };

  const reset = () => {
    // Stop any playing audio
    try {
      cancelTTS();
      if (window && window.speechSynthesis) window.speechSynthesis.cancel();
    } catch (e) {
      console.warn("Error cancelling audio:", e);
    }

    // Stop active speech listening
    if (listeningActiveRef.current) {
      stopListening();
      listeningActiveRef.current = false;
    }

    // Clear entered steps to allow re-execution
    enteredRef.current.clear();

    // Reset walkthrough state to beginning
    dispatch({ type: "RESET" });

    // Reset persisted preferences to defaults
    setTimeout(() => persistChoices(), 0);

    // Call external reset handler if provided
    if (handlers.onReset) {
      try {
        handlers.onReset();
      } catch (e) {
        console.warn("Error calling onReset handler:", e);
      }
    }
  };

  const value = {
    steps: state.steps,
    currentStepIndex: state.currentStepIndex,
    currentStep: (state.steps || [])[state.currentStepIndex] || null,
    listening: state.listening,
    explainer: state.explainer,
    isRunning: state.isRunning,
    next,
    prev,
    goTo,
    reset,
    toggleListening,
    toggleExplainer,
    persistChoices,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
}

export const useWalkthrough = () => {
  const ctx = useContext(WalkthroughContext);
  if (!ctx)
    throw new Error("useWalkthrough must be used within WalkthroughProvider");
  return ctx;
};

export default WalkthroughContext;
